import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Nav from '../components/Nav';
import Footer from '../components/Footer';
import { supabase, SITE_URL } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  ADDON_PRESETS,
  DAYS_OF_WEEK,
  DIETARY_OPTIONS,
  DRINK_DEFAULTS,
  DRINK_OPTIONS,
  SEATING_STYLES,
  TIERS,
  type AddOn,
  type OfferTier,
  type PackageConfig,
  type SeatingStyle,
} from '../lib/database.types';

type Step = 'location' | 'offer' | 'review' | 'done';

type Location = {
  bar_name: string;
  address: string;
  neighborhood: string;
  contact_first_name: string;
  contact_last_name: string;
  contact_email: string;
  contact_phone: string;
  website: string;
  photo_urls: string[];
};

type SelectablePackage = PackageConfig & { selected: boolean };

const DEFAULT_PACKAGES: Record<OfferTier, SelectablePackage> = {
  starter: {
    tier: 'starter',
    selected: false,
    drinks_per_guest: 1,
    drinks_included: [...DRINK_DEFAULTS],
    food_included: true,
    food_appetizers_count: 2,
    food_appetizers_per_guests: 4,
    dietary_options: [],
    price_cents: 3500,
  },
  standard: {
    tier: 'standard',
    selected: true,
    drinks_per_guest: 2,
    drinks_included: [...DRINK_DEFAULTS],
    food_included: true,
    food_appetizers_count: 2,
    food_appetizers_per_guests: 4,
    dietary_options: [],
    price_cents: 5500,
  },
  premium: {
    tier: 'premium',
    selected: false,
    drinks_per_guest: 4,
    drinks_included: [...DRINK_DEFAULTS],
    food_included: true,
    food_appetizers_count: 2,
    food_appetizers_per_guests: 4,
    dietary_options: [],
    price_cents: 9000,
  },
};

export default function Onboarding() {
  // Onboarding is public: vendors fill the form without signing in first.
  // We create their auth account at submit time via signInWithOtp.
  const { profile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('location');
  const [location, setLocation] = useState<Location>(() => ({
    bar_name: '',
    address: '',
    neighborhood: '',
    contact_first_name: profile?.first_name ?? '',
    contact_last_name: profile?.last_name ?? '',
    contact_email: profile?.email ?? '',
    contact_phone: '',
    website: '',
    photo_urls: [],
  }));
  const [offerName, setOfferName] = useState('');
  const [offerDescription, setOfferDescription] = useState('');
  const [daysOfWeek, setDaysOfWeek] = useState<string[]>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [startTime, setStartTime] = useState('16:00');
  const [endTime, setEndTime] = useState('21:00');
  const [headcountMin, setHeadcountMin] = useState(6);
  const [headcountMax, setHeadcountMax] = useState(40);
  const [packages, setPackages] = useState<Record<OfferTier, SelectablePackage>>(DEFAULT_PACKAGES);
  // Setup style and add-ons live at the offer level — they describe the space
  // and the amenities, both of which are shared across package tiers at the
  // same bar. Vendors edit the catalog of available add-ons; the per-guest
  // price they set here is what members see at checkout.
  const [seatingStyle, setSeatingStyle] = useState<SeatingStyle>('mixed');
  const [addOns, setAddOns] = useState<AddOn[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once we've kicked off the vendor account creation at the end of
  // step 1 so the same continue button doesn't double-fire on back/forward.
  const [accountCreated, setAccountCreated] = useState(false);

  // Step 1 → Step 2. Creates the vendor auth account and sends the magic-link
  // + vendor welcome email on the first traversal. Subsequent traversals (back
  // from step 2) skip the network call.
  async function goToOffer() {
    if (!offerName && location.bar_name) {
      setOfferName(`Happy Hour @ ${location.bar_name}`);
    }
    if (!accountCreated) {
      const ok = await createVendorAccount();
      if (!ok) return;
      setAccountCreated(true);
    }
    setStep('offer');
  }

  // Provision the vendor account via Supabase magic-link OTP. The
  // ga_handle_new_user trigger reads `is_vendor_application: true` and
  // stamps the profile with role='vendor', which in turn triggers the
  // vendor_welcome template (instead of the member welcome).
  async function createVendorAccount(): Promise<boolean> {
    setError(null);
    // If the visitor is already signed in we skip account creation.
    const { data: userData } = await supabase.auth.getUser();
    if (userData.user) return true;
    setSubmitting(true);
    const first = location.contact_first_name.trim();
    const last = location.contact_last_name.trim();
    const { error: otpErr } = await supabase.auth.signInWithOtp({
      email: location.contact_email,
      options: {
        shouldCreateUser: true,
        data: {
          first_name: first,
          last_name: last,
          full_name: [first, last].filter(Boolean).join(' '),
          is_vendor_application: 'true',
          bar_name: location.bar_name,
        },
        emailRedirectTo: `${SITE_URL}/auth/callback?next=%2Fonboarding`,
      },
    });
    setSubmitting(false);
    if (otpErr) {
      // Common: Supabase Auth rate-limit when the same email retries
      // before the cooldown. Surface a friendly message; the form stays
      // intact so the vendor can retry.
      setError(
        otpErr.message?.includes('rate')
          ? 'Too many sign-in attempts. Wait a minute and try Continue again.'
          : otpErr.message || 'Could not create your account.',
      );
      return false;
    }
    return true;
  }

  const selectedPackages = (Object.values(packages) as SelectablePackage[]).filter((p) => p.selected);
  const hasMinimumPackage = selectedPackages.length > 0;

  async function submit() {
    setSubmitting(true);
    setError(null);

    const [sh, sm] = startTime.split(':').map(Number);
    const [eh, em] = endTime.split(':').map(Number);
    if (eh * 60 + em - sh * 60 - sm < 120) {
      setError('Arrival window must be at least 2 hours.');
      setSubmitting(false);
      return;
    }
    if (daysOfWeek.length === 0) {
      setError('Pick at least one day.');
      setSubmitting(false);
      return;
    }
    if (!hasMinimumPackage) {
      setError('Pick at least one package.');
      setSubmitting(false);
      return;
    }

    const packagesPayload: PackageConfig[] = selectedPackages.map((p) => ({
      tier: p.tier,
      drinks_per_guest: p.drinks_per_guest,
      drinks_included: p.drinks_included,
      food_included: p.food_included,
      food_appetizers_count: p.food_included ? p.food_appetizers_count : 0,
      food_appetizers_per_guests: p.food_included ? p.food_appetizers_per_guests : 0,
      dietary_options: p.food_included ? p.dietary_options : [],
      price_cents: p.price_cents,
    }));

    // Submit via SECURITY DEFINER RPC. The function reads applicant_user_id
    // from auth.uid() server-side; for visitors who created the account at
    // step 1 but haven't clicked the magic link yet, applicant_user_id stays
    // null and is backfilled by ga_handle_new_user on first sign-in.
    const contactFull = [location.contact_first_name, location.contact_last_name]
      .map((s) => s.trim()).filter(Boolean).join(' ');
    const { data: insertedId, error: insErr } = await supabase.rpc(
      'ga_submit_vendor_application',
      {
        payload: {
          bar_name: location.bar_name,
          contact_name: contactFull,
          contact_email: location.contact_email,
          phone: location.contact_phone || null,
          neighborhood: location.neighborhood || null,
          address: location.address || null,
          website: location.website || null,
          offer_title: offerName,
          offer_description: offerDescription || null,
          headcount_min: String(headcountMin),
          headcount_max: String(headcountMax),
          days_of_week: daysOfWeek,
          start_time: `${startTime}:00`,
          end_time: `${endTime}:00`,
          photo_urls: location.photo_urls,
          packages: packagesPayload,
          seating_style: seatingStyle,
          add_ons: addOns
            .filter((a) => a.label.trim() !== '' && a.price_cents_per_guest >= 0)
            .map((a) => ({
              key: a.key,
              label: a.label.trim(),
              price_cents_per_guest: a.price_cents_per_guest,
            })),
        },
      },
    );

    if (insErr || !insertedId) {
      setError(insErr?.message ?? 'Could not submit application.');
      setSubmitting(false);
      return;
    }
    const applicationId = insertedId as string;

    // Notify admin + applicant. Anon callers are allowed for this kind when
    // the application was submitted without applicant_user_id.
    try {
      await supabase.functions.invoke('ga-notify', {
        body: { kind: 'onboarding_submitted', application_id: applicationId },
      });
    } catch { /* non-fatal */ }

    setSubmitting(false);
    setStep('done');
  }

  return (
    <>
      <Nav />
      <section style={{ paddingTop: 56, paddingBottom: 24 }}>
        <div className="container">
          <div className="eyebrow" style={{ marginBottom: 18 }}>
            List your spot · {step === 'location' ? 'Step 1 of 3' : step === 'offer' ? 'Step 2 of 3' : step === 'review' ? 'Step 3 of 3' : 'Submitted'}
          </div>
          <h1 className="display" style={{ fontSize: 'clamp(36px, 5vw, 60px)', margin: 0, maxWidth: 720 }}>
            {step === 'location' && <>Your <span className="serif-italic" style={{ color: 'var(--wine)' }}>spot.</span></>}
            {step === 'offer' && <>Build your <span className="serif-italic" style={{ color: 'var(--wine)' }}>happy hour.</span></>}
            {step === 'review' && <>One last <span className="serif-italic" style={{ color: 'var(--wine)' }}>look.</span></>}
            {step === 'done' && <>You're in <span className="serif-italic" style={{ color: 'var(--wine)' }}>review.</span></>}
          </h1>
          <StepBar step={step} />
        </div>
      </section>

      <section style={{ paddingBottom: 120 }}>
        <div className="container">
          {step === 'location' && (
            <div style={{ maxWidth: 760 }}>
              <LocationStep
                value={location}
                onChange={setLocation}
                onNext={goToOffer}
                submitting={submitting}
                error={error}
              />
            </div>
          )}

          {step === 'offer' && (
            <div className="r-split-wide">

              <OfferStep
                photoUrls={location.photo_urls}
                onChangePhotos={(photos) => setLocation({ ...location, photo_urls: photos })}
                offerName={offerName} setOfferName={setOfferName}
                offerDescription={offerDescription} setOfferDescription={setOfferDescription}
                daysOfWeek={daysOfWeek} setDaysOfWeek={setDaysOfWeek}
                startTime={startTime} setStartTime={setStartTime}
                endTime={endTime} setEndTime={setEndTime}
                headcountMin={headcountMin} setHeadcountMin={setHeadcountMin}
                headcountMax={headcountMax} setHeadcountMax={setHeadcountMax}
                seatingStyle={seatingStyle} setSeatingStyle={setSeatingStyle}
                addOns={addOns} setAddOns={setAddOns}
                packages={packages} setPackages={setPackages}
                onBack={() => setStep('location')}
                onNext={() => setStep('review')}
              />
              <PricingSidebar
                packages={packages}
                setPackages={setPackages}
                onNext={() => setStep('review')}
                disabled={!hasMinimumPackage}
              />
            </div>
          )}

          {step === 'review' && (
            <div style={{ maxWidth: 760 }}>
              <ReviewStep
                location={location}
                offerName={offerName}
                offerDescription={offerDescription}
                daysOfWeek={daysOfWeek}
                startTime={startTime}
                endTime={endTime}
                headcountMin={headcountMin}
                headcountMax={headcountMax}
                seatingStyle={seatingStyle}
                addOns={addOns}
                packages={selectedPackages}
                agreedToTerms={agreedToTerms}
                setAgreedToTerms={setAgreedToTerms}
                error={error}
                submitting={submitting}
                onBack={() => setStep('offer')}
                onSubmit={submit}
              />
            </div>
          )}

          {step === 'done' && (
            <div style={{ maxWidth: 760 }}>
              <DoneStep onGo={() => navigate('/dashboard')} location={location} />
            </div>
          )}
        </div>
      </section>
      <Footer />
    </>
  );
}

function StepBar({ step }: { step: Step }) {
  const idx = step === 'location' ? 0 : step === 'offer' ? 1 : step === 'review' ? 2 : 3;
  return (
    <div style={{ display: 'flex', gap: 8, marginTop: 32, maxWidth: 460 }}>
      {[0, 1, 2].map((i) => (
        <div key={i} style={{ flex: 1, height: 6, borderRadius: 3, background: i <= idx ? 'var(--ink)' : 'var(--line)' }} />
      ))}
    </div>
  );
}

// ---------- Step 1: Location ----------
function LocationStep({
  value,
  onChange,
  onNext,
  submitting,
  error,
}: {
  value: Location;
  onChange: (v: Location) => void;
  onNext: () => void;
  submitting: boolean;
  error: string | null;
}) {
  const valid =
    value.bar_name &&
    value.address &&
    value.contact_first_name &&
    value.contact_last_name &&
    value.contact_email &&
    value.contact_phone;
  return (
    <form onSubmit={(e: FormEvent) => { e.preventDefault(); onNext(); }} style={{ padding: 32, borderRadius: 18, background: 'var(--card)' }}>
      <SectionTitle title="The basics" hint="Where teams will be heading." />
      <div className="stack">
        <Field label="Business name" required hint="What teams will see on the listing.">
          <input value={value.bar_name} onChange={(e) => onChange({ ...value, bar_name: e.target.value })} placeholder="e.g. Goa's" required />
        </Field>
        <Field label="Address" required hint="Start typing; we'll find it and figure out the neighborhood.">
          <AddressAutocomplete
            value={value.address}
            onChange={(address, neighborhood) =>
              onChange({
                ...value,
                address,
                // Only overwrite neighborhood when the autocomplete returned one.
                neighborhood: neighborhood || value.neighborhood,
              })
            }
          />
        </Field>
        <Field label="Email" required hint="For your sign-in link and booking notifications.">
          <input type="email" value={value.contact_email} onChange={(e) => onChange({ ...value, contact_email: e.target.value })} placeholder="you@yourbar.com" required />
        </Field>
      </div>

      <hr className="divider" />
      <SectionTitle title="Contact person" hint="This is not shared with guests." />
      <div className="stack">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <Field label="First name" required>
            <input value={value.contact_first_name} onChange={(e) => onChange({ ...value, contact_first_name: e.target.value })} required />
          </Field>
          <Field label="Last name" required>
            <input value={value.contact_last_name} onChange={(e) => onChange({ ...value, contact_last_name: e.target.value })} required />
          </Field>
        </div>
        <Field label="Phone" required hint="Goaround uses this only to contact you directly. Never shared with guests.">
          <input type="tel" value={value.contact_phone} onChange={(e) => onChange({ ...value, contact_phone: e.target.value })} placeholder="(212) 555-0100" required />
        </Field>
        <Field label="Website" hint="Optional.">
          <input value={value.website} onChange={(e) => onChange({ ...value, website: e.target.value })} placeholder="https://" />
        </Field>
      </div>

      {error && <div className="field-error" style={{ marginTop: 24 }}>{error}</div>}

      <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 10 }}>
        <button type="submit" className="btn btn-primary" disabled={!valid || submitting}>
          {submitting ? 'Creating your account…' : 'Continue →'}
        </button>
        <div style={{ fontSize: 12, color: 'var(--ink-soft)', maxWidth: 360, textAlign: 'right' }}>
          By continuing you are agreeing to our{' '}
          <Link to="/terms" style={{ color: 'var(--ink)' }}>terms</Link> and{' '}
          <Link to="/privacy" style={{ color: 'var(--ink)' }}>privacy policy</Link>.
        </div>
      </div>
    </form>
  );
}

function PhotoEditor({ value, onChange, max = 3 }: { value: string[]; onChange: (v: string[]) => void; max?: number }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const slotsLeft = max - value.length;
  const full = slotsLeft <= 0;

  async function handleFiles(files: FileList | File[] | null) {
    if (!files) return;
    setError(null);
    if (slotsLeft <= 0) {
      setError(`Up to ${max} photos.`);
      return;
    }
    const arr = Array.from(files).slice(0, slotsLeft);
    setUploading(true);
    const uploaded: string[] = [];
    // All uploads go through the ga-upload-photo edge function, which enforces
    // per-IP rate limits (20/hour), file-size and MIME caps, then writes to
    // the bucket via service role. Direct anon writes to the bucket are no
    // longer allowed.
    for (const f of arr) {
      if (!f.type.startsWith('image/')) {
        setError(`${f.name} isn't an image.`);
        continue;
      }
      if (f.size > 5 * 1024 * 1024) {
        setError(`${f.name} is over 5 MB.`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      const { data, error: fnErr } = await supabase.functions.invoke('ga-upload-photo', { body: fd });
      const result = data as { ok?: boolean; url?: string; error?: string; detail?: string } | null;
      if (fnErr || !result?.ok) {
        const msg = result?.detail || result?.error || fnErr?.message || 'Upload failed.';
        setError(msg === 'rate_limited' ? 'Too many uploads. Try again in an hour.' : msg);
        continue;
      }
      if (result.url) uploaded.push(result.url);
    }
    if (uploaded.length > 0) onChange([...value, ...uploaded]);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  }

  function remove(i: number) {
    onChange(value.filter((_, j) => j !== i));
    setError(null);
  }

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFiles(e.target.files)}
      />
      <div
        role="button"
        tabIndex={0}
        onClick={() => !full && !uploading && inputRef.current?.click()}
        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !full && !uploading) { e.preventDefault(); inputRef.current?.click(); } }}
        onDragOver={(e) => { e.preventDefault(); if (!full) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); if (!full && !uploading) handleFiles(e.dataTransfer.files); }}
        style={{
          padding: '24px 16px',
          border: `2px dashed ${dragging ? 'var(--ink)' : 'var(--line)'}`,
          borderRadius: 14,
          textAlign: 'center',
          cursor: full || uploading ? 'not-allowed' : 'pointer',
          background: dragging ? 'color-mix(in oklch, var(--primary) 12%, transparent)' : 'var(--bg)',
          color: full ? 'var(--ink-soft)' : 'var(--ink)',
          opacity: uploading ? 0.7 : 1,
          transition: 'background 0.15s ease, border-color 0.15s ease',
        }}
      >
        {uploading ? (
          <div style={{ fontWeight: 600 }}>Uploading...</div>
        ) : full ? (
          <div style={{ fontWeight: 500, fontSize: 14 }}>Maximum {max} photos.</div>
        ) : (
          <>
            <div style={{ fontWeight: 600, fontSize: 15 }}>Drag photos here or click to upload</div>
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 4 }}>
              JPG or PNG · up to {slotsLeft} more · 5 MB each
            </div>
          </>
        )}
      </div>

      {error && <div className="field-error" style={{ marginTop: 8 }}>{error}</div>}

      {value.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${max}, 1fr)`, gap: 12, marginTop: 14 }}>
          {value.map((u, i) => (
            <div key={i} style={{ position: 'relative', aspectRatio: '1', borderRadius: 12, background: `url(${u}) center/cover`, border: '1px solid var(--line)' }}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); remove(i); }}
                aria-label="Remove photo"
                style={{ position: 'absolute', top: 6, right: 6, background: 'rgba(0,0,0,0.7)', color: 'white', border: 'none', borderRadius: '50%', width: 24, height: 24, cursor: 'pointer', fontSize: 14, lineHeight: '20px', padding: 0 }}
              >×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------- Address autocomplete ----------
type NominatimAddress = {
  house_number?: string;
  road?: string;
  neighbourhood?: string;
  suburb?: string;
  quarter?: string;
  city_district?: string;
  city?: string;
  town?: string;
  state?: string;
  postcode?: string;
  country?: string;
};
type NominatimResult = {
  display_name: string;
  lat: string;
  lon: string;
  address: NominatimAddress;
};

function AddressAutocomplete({
  value,
  onChange,
}: {
  value: string;
  onChange: (address: string, neighborhood: string) => void;
}) {
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<NominatimResult[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced address lookup via OpenStreetMap Nominatim. Free, no API key,
  // 1 req/sec policy; debounce keeps us under it. NYC viewbox biases results.
  useEffect(() => {
    if (!query || query.length < 4) {
      setSuggestions([]);
      return;
    }
    if (query === value && !open) return;
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&countrycodes=us&viewbox=-74.05,40.85,-73.85,40.65&bounded=1&limit=5&q=${encodeURIComponent(query)}`;
        const res = await fetch(url, { headers: { 'Accept-Language': 'en' } });
        if (!res.ok) throw new Error('search_failed');
        const data: NominatimResult[] = await res.json();
        setSuggestions(data);
        setOpen(true);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Close on outside click.
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Build the AirBnB-style two-line entry from a Nominatim result.
  // Main line: "<house#> <road>" — the part the user types.
  // Secondary: "<neighborhood/city>, <state> <postcode>" — context.
  function describe(s: NominatimResult): { main: string; secondary: string } {
    const a = s.address;
    const street = [a.house_number, a.road].filter(Boolean).join(' ').trim();
    const main = street || s.display_name.split(',')[0];
    const locality = a.neighbourhood || a.suburb || a.quarter || a.city_district || a.city || a.town || '';
    const region = [a.state, a.postcode].filter(Boolean).join(' ');
    const secondary = [locality, region].filter(Boolean).join(', ');
    return { main, secondary: secondary || s.display_name };
  }

  function pick(s: NominatimResult) {
    const { main, secondary } = describe(s);
    // Use the structured form ("street, locality, state postcode") rather
    // than Nominatim's verbose display_name — it's what AirBnB-style pickers
    // commit to the field, and what vendors want to see in the listing.
    const formatted = [main, secondary].filter(Boolean).join(', ');
    const neighborhood =
      s.address.neighbourhood ||
      s.address.suburb ||
      s.address.quarter ||
      s.address.city_district ||
      '';
    setQuery(formatted);
    onChange(formatted, neighborhood);
    setOpen(false);
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <input
        value={query}
        onChange={(e) => {
          const v = e.target.value;
          setQuery(v);
          // Sync raw value back so submit sees it even if user doesn't pick.
          // Don't clear neighborhood until they pick a different address.
          onChange(v, '');
          setOpen(true);
        }}
        onFocus={() => { if (suggestions.length > 0) setOpen(true); }}
        placeholder="33 E 33rd St, New York, NY 10016"
        autoComplete="off"
        required
      />
      {loading && (
        <div style={{ position: 'absolute', right: 14, top: 14, fontSize: 11, color: 'var(--ink-soft)' }}>
          searching...
        </div>
      )}
      {open && suggestions.length > 0 && (
        <ul style={{
          listStyle: 'none', padding: 6, margin: 0,
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, right: 0,
          background: 'var(--bg)', border: '1px solid var(--line)', borderRadius: 16,
          zIndex: 20, maxHeight: 320, overflowY: 'auto',
          boxShadow: '0 16px 40px rgba(0,0,0,0.14)',
        }}>
          {suggestions.map((s, i) => {
            const { main, secondary } = describe(s);
            return (
              <li
                key={`${s.lat}-${s.lon}-${i}`}
                onClick={() => pick(s)}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '28px 1fr',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 14px',
                  borderRadius: 10,
                  cursor: 'pointer',
                  transition: 'background 0.12s ease',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--card)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span
                  aria-hidden
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    borderRadius: 8,
                    background: 'var(--card)',
                    color: 'var(--ink-soft)',
                    flex: '0 0 28px',
                  }}
                >
                  {/* Map-pin glyph; inline SVG keeps the dropdown dependency-free. */}
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 10c0 7-8 13-8 13s-8-6-8-13a8 8 0 1 1 16 0Z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </span>
                <span style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {main}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {secondary}
                  </div>
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---------- Step 2: Offer ----------
type OfferStepProps = {
  // Photos are part of the Location object, but we collect them at the top
  // of step 2 (they're more relevant alongside the offer copy than alongside
  // contact info). We keep the same `photo_urls` field so downstream
  // consumers — ga_submit_vendor_application, ga_approve_vendor_application —
  // see no change.
  photoUrls: string[]; onChangePhotos: (v: string[]) => void;
  offerName: string; setOfferName: (v: string) => void;
  offerDescription: string; setOfferDescription: (v: string) => void;
  daysOfWeek: string[]; setDaysOfWeek: (v: string[]) => void;
  startTime: string; setStartTime: (v: string) => void;
  endTime: string; setEndTime: (v: string) => void;
  headcountMin: number; setHeadcountMin: (v: number) => void;
  headcountMax: number; setHeadcountMax: (v: number) => void;
  seatingStyle: SeatingStyle; setSeatingStyle: (v: SeatingStyle) => void;
  addOns: AddOn[]; setAddOns: (v: AddOn[]) => void;
  packages: Record<OfferTier, SelectablePackage>; setPackages: (v: Record<OfferTier, SelectablePackage>) => void;
  onBack: () => void;
  onNext: () => void;
};

function OfferStep(p: OfferStepProps) {
  function toggleDay(d: string) {
    p.setDaysOfWeek(p.daysOfWeek.includes(d) ? p.daysOfWeek.filter((x) => x !== d) : [...p.daysOfWeek, d]);
  }
  function togglePackage(tier: OfferTier) {
    p.setPackages({ ...p.packages, [tier]: { ...p.packages[tier], selected: !p.packages[tier].selected } });
  }
  function updatePackage(tier: OfferTier, patch: Partial<SelectablePackage>) {
    p.setPackages({ ...p.packages, [tier]: { ...p.packages[tier], ...patch } });
  }
  const selectedCount = (Object.values(p.packages) as SelectablePackage[]).filter((x) => x.selected).length;

  return (
    <div style={{ padding: 32, borderRadius: 18, background: 'var(--card)' }}>
      <div style={{ padding: 24, borderRadius: 14, background: 'var(--bg)', border: '1px solid var(--line)', marginBottom: 24 }}>
        <SectionTitle title="Photos" hint="At least one helps teams imagine the space. Up to 3." />
        <PhotoEditor value={p.photoUrls} onChange={p.onChangePhotos} />
      </div>

      <SectionTitle title="What's it called?" />
      <div className="stack">
        <Field label="Package name" hint="What teams see. We pre-filled it. Feel free to change.">
          <input value={p.offerName} onChange={(e) => p.setOfferName(e.target.value)} placeholder="Happy Hour @ Your Bar" />
        </Field>
        <Field label="Description" hint="Optional. Set expectations for the team.">
          <textarea rows={2} value={p.offerDescription} onChange={(e) => p.setOfferDescription(e.target.value)} placeholder="What's the vibe? Anything to know?" />
        </Field>
      </div>

      <hr className="divider" />
      <SectionTitle title="When can teams come by?" hint="Defaults to weekdays. Pick the days that work." />
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {DAYS_OF_WEEK.map((d) => {
          const on = p.daysOfWeek.includes(d);
          return (
            <button
              key={d}
              type="button"
              onClick={() => toggleDay(d)}
              className="mono"
              style={{
                padding: '10px 16px',
                borderRadius: 999,
                border: '1px solid var(--ink)',
                background: on ? 'var(--ink)' : 'transparent',
                color: on ? 'var(--bg)' : 'var(--ink)',
                fontSize: 12,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                cursor: 'pointer',
              }}
            >{d}</button>
          );
        })}
      </div>

      <hr className="divider" />
      <SectionTitle title="Arrival time" hint="The window guests can show up. 2 hour minimum." />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <Field label="Start">
          <TimeSelect value={p.startTime} onChange={p.setStartTime} startHour={12} endHour={22} />
        </Field>
        <Field label="End">
          <TimeSelect value={p.endTime} onChange={p.setEndTime} startHour={14} endHour={23} />
        </Field>
      </div>

      <hr className="divider" />
      <SectionTitle title="Group size" hint="The range you'll accept per booking." />
      <RangeSlider
        min={2} max={100}
        valueMin={p.headcountMin} valueMax={p.headcountMax}
        onChange={(mn, mx) => { p.setHeadcountMin(mn); p.setHeadcountMax(mx); }}
      />

      <hr className="divider" />
      <SectionTitle title="What's the setup?" hint="Tell members what to expect when their team walks in." />
      <SeatingPicker value={p.seatingStyle} onChange={p.setSeatingStyle} />

      <hr className="divider" />
      <SectionTitle title="Add-on amenities" hint="Optional upgrades members can opt into at checkout. Per-guest pricing." />
      <AddOnsEditor value={p.addOns} onChange={p.setAddOns} />

      <hr className="divider" />
      <SectionTitle title="Pick a package tier" hint="At least one. Click again to deselect. Your changes stick." />
      <div className="r-grid-3">
        {TIERS.map((t) => {
          const pkg = p.packages[t.tier];
          const on = pkg.selected;
          return (
            <button
              key={t.tier}
              type="button"
              onClick={() => togglePackage(t.tier)}
              style={{
                position: 'relative',
                padding: '20px 16px',
                borderRadius: 16,
                border: on ? '2px solid var(--ink)' : '1.5px solid var(--line)',
                background: on ? 'var(--ink)' : 'transparent',
                color: on ? 'var(--bg)' : 'var(--ink)',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ position: 'absolute', top: 12, right: 12, width: 20, height: 20, borderRadius: '50%', border: `2px solid ${on ? 'var(--bg)' : 'var(--line)'}`, background: on ? 'var(--primary)' : 'transparent' }} />
              <div className="display" style={{ fontSize: 22, marginBottom: 6 }}>{t.name}</div>
              <div style={{ fontSize: 13, opacity: 0.8 }}>{t.tagline}</div>
            </button>
          );
        })}
      </div>
      {selectedCount === 0 && (
        <div style={{ marginTop: 12, color: 'var(--error)', fontSize: 14 }}>Pick at least one package to continue.</div>
      )}

      {selectedCount > 0 && (
        <>
          <hr className="divider" />
          <SectionTitle title="Configure your offer" hint="Tweak per tier. Defaults are sensible. Most spots leave them." />
          <div className="stack">
            {TIERS.filter((t) => p.packages[t.tier].selected).map((t) => (
              <PackageEditor
                key={t.tier}
                tier={t}
                value={p.packages[t.tier]}
                onChange={(patch) => updatePackage(t.tier, patch)}
              />
            ))}
          </div>
        </>
      )}

      <div className="row" style={{ justifyContent: 'space-between', marginTop: 32 }}>
        <button type="button" onClick={p.onBack} className="btn btn-ghost">← Back</button>
        <button type="button" onClick={p.onNext} className="btn btn-primary" disabled={selectedCount === 0}>Review →</button>
      </div>
    </div>
  );
}

function SeatingPicker({ value, onChange }: { value: SeatingStyle; onChange: (v: SeatingStyle) => void }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
      {SEATING_STYLES.map((s) => {
        const on = value === s.value;
        return (
          <button
            key={s.value}
            type="button"
            onClick={() => onChange(s.value)}
            style={{
              padding: '14px 16px',
              borderRadius: 14,
              border: on ? '2px solid var(--ink)' : '1.5px solid var(--line)',
              background: on ? 'var(--ink)' : 'transparent',
              color: on ? 'var(--bg)' : 'var(--ink)',
              textAlign: 'left',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{s.label}</div>
            <div style={{ fontSize: 12, opacity: 0.78 }}>{s.tagline}</div>
          </button>
        );
      })}
    </div>
  );
}

function AddOnsEditor({ value, onChange }: { value: AddOn[]; onChange: (v: AddOn[]) => void }) {
  // The editor renders the preset catalog as toggleable rows. Picking one drops
  // it into `value` with the preset price; the vendor can override the per-guest
  // price inline. Toggling off removes it from `value` entirely.
  const valueByKey = new Map(value.map((a) => [a.key, a] as const));
  function toggle(preset: AddOn) {
    if (valueByKey.has(preset.key)) {
      onChange(value.filter((a) => a.key !== preset.key));
    } else {
      onChange([...value, { ...preset }]);
    }
  }
  function setPrice(key: string, dollars: number) {
    onChange(value.map((a) => a.key === key ? { ...a, price_cents_per_guest: Math.max(0, Math.round(dollars * 100)) } : a));
  }
  function setLabel(key: string, label: string) {
    onChange(value.map((a) => a.key === key ? { ...a, label } : a));
  }
  return (
    <div style={{ display: 'grid', gap: 10 }}>
      {ADDON_PRESETS.map((preset) => {
        const picked = valueByKey.get(preset.key);
        const on = !!picked;
        return (
          <div
            key={preset.key}
            style={{
              padding: '12px 14px',
              borderRadius: 12,
              border: on ? '1.5px solid var(--ink)' : '1px solid var(--line)',
              background: on ? 'var(--bg)' : 'transparent',
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto',
              alignItems: 'center',
              gap: 14,
              transition: 'border-color 0.15s ease',
            }}
          >
            <button
              type="button"
              onClick={() => toggle(preset)}
              aria-pressed={on}
              style={{
                width: 22, height: 22, borderRadius: 6,
                border: `2px solid ${on ? 'var(--ink)' : 'var(--line)'}`,
                background: on ? 'var(--ink)' : 'transparent',
                color: on ? 'var(--bg)' : 'transparent',
                cursor: 'pointer', padding: 0,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 900,
              }}
            >{on ? '✓' : ''}</button>
            {on ? (
              <input
                value={picked!.label}
                onChange={(e) => setLabel(preset.key, e.target.value)}
                style={{ height: 'auto', padding: '6px 10px', fontWeight: 600 }}
              />
            ) : (
              <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{preset.label}</div>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ color: 'var(--ink-soft)', fontSize: 13 }}>$</span>
              <input
                type="number"
                min={0}
                step={1}
                disabled={!on}
                value={(picked?.price_cents_per_guest ?? preset.price_cents_per_guest) / 100}
                onChange={(e) => setPrice(preset.key, Number(e.target.value) || 0)}
                style={{ width: 64, height: 'auto', padding: '6px 8px', textAlign: 'right' }}
              />
              <span style={{ color: 'var(--ink-soft)', fontSize: 13, whiteSpace: 'nowrap' }}>/guest</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PackageEditor({ tier, value, onChange }: { tier: { tier: OfferTier; name: string; tagline: string }; value: SelectablePackage; onChange: (patch: Partial<SelectablePackage>) => void }) {
  function toggleDrink(d: string) {
    onChange({ drinks_included: value.drinks_included.includes(d) ? value.drinks_included.filter((x) => x !== d) : [...value.drinks_included, d] });
  }
  function toggleDietary(d: string) {
    onChange({ dietary_options: value.dietary_options.includes(d) ? value.dietary_options.filter((x) => x !== d) : [...value.dietary_options, d] });
  }
  return (
    <div style={{ padding: 20, borderRadius: 14, background: 'var(--bg)', border: '1px solid var(--line)' }}>
      <div className="row-between" style={{ marginBottom: 16 }}>
        <div className="display" style={{ fontSize: 20 }}>{tier.name}</div>
        <span className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>{tier.tier}</span>
      </div>

      <Field label="Drinks per guest" hint="What each guest can order from the menu below.">
        <input type="number" min={0} max={10} value={value.drinks_per_guest} onChange={(e) => onChange({ drinks_per_guest: Math.max(0, Number(e.target.value) || 0) })} style={{ maxWidth: 120 }} />
      </Field>

      <div style={{ marginTop: 16 }}>
        <div className="field-label">Drinks included</div>
        <div className="field-hint" style={{ marginTop: -2, marginBottom: 8 }}>What guests can pick from. Multi-select.</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {DRINK_OPTIONS.map((d) => {
            const on = value.drinks_included.includes(d);
            return (
              <button
                key={d}
                type="button"
                onClick={() => toggleDrink(d)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 999,
                  border: '1px solid var(--ink)',
                  background: on ? 'var(--primary)' : 'transparent',
                  color: 'var(--ink)',
                  fontSize: 13,
                  cursor: 'pointer',
                }}
              >{on ? '✓ ' : ''}{d}</button>
            );
          })}
        </div>
      </div>

      <div style={{ marginTop: 20 }}>
        <label className="row" style={{ cursor: 'pointer', gap: 10 }}>
          <input
            type="checkbox"
            checked={value.food_included}
            onChange={(e) => onChange({ food_included: e.target.checked })}
            style={{ width: 'auto', margin: 0 }}
          />
          <span style={{ fontWeight: 600 }}>Include food</span>
          <span className="muted" style={{ fontSize: 14 }}>Recommended for groups over 8.</span>
        </label>
      </div>

      {value.food_included && (
        <div style={{ marginTop: 12, padding: 16, background: 'var(--card)', borderRadius: 10 }}>
          <div className="field-hint" style={{ marginBottom: 12 }}>How many appetizers come with the group?</div>
          <div className="row" style={{ flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <input
              type="number"
              min={1}
              value={value.food_appetizers_count}
              onChange={(e) => onChange({ food_appetizers_count: Math.max(1, Number(e.target.value) || 1) })}
              style={{ width: 70, textAlign: 'center' }}
            />
            <span>shared appetizers per</span>
            <input
              type="number"
              min={1}
              value={value.food_appetizers_per_guests}
              onChange={(e) => onChange({ food_appetizers_per_guests: Math.max(1, Number(e.target.value) || 1) })}
              style={{ width: 70, textAlign: 'center' }}
            />
            <span>people</span>
          </div>

          <div style={{ marginTop: 16 }}>
            <div className="field-label">Dietary options</div>
            <div className="field-hint" style={{ marginTop: -2, marginBottom: 8 }}>What you can accommodate. Optional.</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {DIETARY_OPTIONS.map((d) => {
                const on = value.dietary_options.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    onClick={() => toggleDietary(d)}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 999,
                      border: '1px solid var(--ink)',
                      background: on ? 'var(--primary)' : 'transparent',
                      color: 'var(--ink)',
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >{on ? '✓ ' : ''}{d}</button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PricingSidebar({ packages, setPackages, onNext, disabled }: { packages: Record<OfferTier, SelectablePackage>; setPackages: (v: Record<OfferTier, SelectablePackage>) => void; onNext: () => void; disabled: boolean }) {
  const selected = useMemo(
    () => TIERS.map((t) => ({ ...t, pkg: packages[t.tier] })).filter((x) => x.pkg.selected),
    [packages],
  );
  function setPrice(tier: OfferTier, dollars: number) {
    setPackages({ ...packages, [tier]: { ...packages[tier], price_cents: Math.round(dollars * 100) } });
  }
  return (
    <aside style={{ position: 'sticky', top: 24, alignSelf: 'start', padding: 24, borderRadius: 18, background: 'var(--ink)', color: 'var(--bg)' }}>
      <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--primary)', marginBottom: 8 }}>Set your price</div>
      <div className="display" style={{ fontSize: 26, marginBottom: 16 }}>Subtotal per head</div>
      <p style={{ fontSize: 13, color: 'color-mix(in oklch, var(--bg) 70%, transparent)', margin: '0 0 20px' }}>
        Tax and gratuity are added at checkout. Just the subtotal here.
      </p>

      {selected.length === 0 ? (
        <div style={{ padding: 16, background: 'color-mix(in oklch, var(--bg) 8%, transparent)', borderRadius: 10, fontSize: 14, color: 'color-mix(in oklch, var(--bg) 80%, transparent)' }}>
          Pick a package tier to set pricing.
        </div>
      ) : (
        <div className="stack-sm">
          {selected.map(({ tier, name, pkg }) => {
            const dollars = pkg.price_cents / 100;
            const ratio = pkg.drinks_per_guest > 0 ? dollars / pkg.drinks_per_guest : Infinity;
            const score = ratio > 15 ? 'high' : ratio > 12 ? 'mid' : 'good';
            const color = score === 'high' ? '#ff6b5b' : score === 'mid' ? '#f5c14b' : '#7cd87c';
            const label = score === 'high' ? 'Pricey' : score === 'mid' ? 'On the high end' : 'Solid value';
            return (
              <div key={tier} style={{ padding: 14, background: 'color-mix(in oklch, var(--bg) 8%, transparent)', borderRadius: 12 }}>
                <div className="row-between" style={{ marginBottom: 8 }}>
                  <div style={{ fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 12, color }}>● {label}</div>
                </div>
                <div className="row" style={{ alignItems: 'center', gap: 8 }}>
                  <span style={{ color: 'color-mix(in oklch, var(--bg) 70%, transparent)' }}>$</span>
                  <input
                    type="number"
                    min={0}
                    step={1}
                    value={dollars}
                    onChange={(e) => setPrice(tier, Math.max(0, Number(e.target.value) || 0))}
                    style={{ flex: 1, background: 'transparent', color: 'var(--bg)', borderColor: 'color-mix(in oklch, var(--bg) 20%, transparent)' }}
                  />
                  <span style={{ fontSize: 13, color: 'color-mix(in oklch, var(--bg) 70%, transparent)' }}>/head</span>
                </div>
                <div style={{ marginTop: 8, fontSize: 13, color: 'color-mix(in oklch, var(--bg) 70%, transparent)' }}>
                  12 guests → <strong style={{ color: 'var(--bg)' }}>${(dollars * 12).toFixed(0)}</strong> subtotal
                </div>
              </div>
            );
          })}
        </div>
      )}

      <button
        type="button"
        onClick={onNext}
        disabled={disabled}
        className="btn btn-pop"
        style={{ width: '100%', justifyContent: 'center', marginTop: 20 }}
      >Review →</button>
    </aside>
  );
}

// ---------- Step 3: Review ----------
type ReviewProps = {
  location: Location;
  offerName: string;
  offerDescription: string;
  daysOfWeek: string[];
  startTime: string;
  endTime: string;
  headcountMin: number;
  headcountMax: number;
  seatingStyle: SeatingStyle;
  addOns: AddOn[];
  packages: SelectablePackage[];
  agreedToTerms: boolean;
  setAgreedToTerms: (v: boolean) => void;
  error: string | null;
  submitting: boolean;
  onBack: () => void;
  onSubmit: () => void;
};

function ReviewStep(p: ReviewProps) {
  return (
    <div className="stack">
      <Panel title="Location">
        <Row k="Name" v={p.location.bar_name} />
        <Row k="Address" v={p.location.address} />
        <Row k="Neighborhood" v={p.location.neighborhood || '-'} />
        <Row
          k="Contact"
          v={`${[p.location.contact_first_name, p.location.contact_last_name].filter(Boolean).join(' ')} · ${p.location.contact_email} · ${p.location.contact_phone}`}
        />
        {p.location.photo_urls.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 12 }}>
            {p.location.photo_urls.slice(0, 6).map((u, i) => (
              <div key={i} style={{ width: 56, height: 56, borderRadius: 8, background: `url(${u}) center/cover`, border: '1px solid var(--line)' }} />
            ))}
          </div>
        )}
      </Panel>

      <Panel title="Happy hour">
        <Row k="Name" v={p.offerName} />
        <Row k="Days" v={p.daysOfWeek.join(', ')} />
        <Row k="Arrival window" v={`${formatHour(p.startTime)} – ${formatHour(p.endTime)}`} />
        <Row k="Group size" v={`${p.headcountMin} – ${p.headcountMax} guests`} />
        <Row k="Setup" v={SEATING_STYLES.find((s) => s.value === p.seatingStyle)?.label ?? p.seatingStyle} />
      </Panel>

      {p.addOns.length > 0 && (
        <Panel title="Add-ons">
          {p.addOns.map((a) => (
            <Row key={a.key} k={a.label} v={`+$${(a.price_cents_per_guest / 100).toFixed(0)} /guest`} />
          ))}
        </Panel>
      )}

      {p.packages.map((pkg) => {
        const t = TIERS.find((x) => x.tier === pkg.tier)!;
        return (
          <Panel key={pkg.tier} title={`${t.name} Round`}>
            <Row k="Price" v={`$${(pkg.price_cents / 100).toFixed(0)}/head`} />
            <Row k="Drinks" v={`${pkg.drinks_per_guest} per guest · ${pkg.drinks_included.join(', ')}`} />
            {pkg.food_included && <Row k="Food" v={`${pkg.food_appetizers_count} shared appetizers per ${pkg.food_appetizers_per_guests} people${pkg.dietary_options.length > 0 ? ` · ${pkg.dietary_options.join(', ')}` : ''}`} />}
          </Panel>
        );
      })}

      <div style={{ padding: 24, borderRadius: 18, background: 'var(--card)' }}>
        <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 14 }}>Terms all hosts agree to</div>
        <ul style={{ paddingLeft: 20, color: 'var(--ink)', marginTop: 0, lineHeight: 1.6 }}>
          <li>Final headcount and cancellation allowed up to <strong>3pm on the day of the reservation</strong>.</li>
          <li>Pre-paid packages are settled by Goaround after the event runs.</li>
          <li>Off-package orders are billed separately at the bar.</li>
        </ul>
        <label className="row" style={{ marginTop: 14, cursor: 'pointer', gap: 8 }}>
          <input type="checkbox" checked={p.agreedToTerms} onChange={(e) => p.setAgreedToTerms(e.target.checked)} style={{ width: 'auto', margin: 0 }} />
          <span>I agree to these terms for my listing.</span>
        </label>
      </div>

      {p.error && <div className="field-error">{p.error}</div>}

      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button onClick={p.onBack} className="btn btn-ghost">← Back</button>
        <button onClick={p.onSubmit} className="btn btn-pop" disabled={!p.agreedToTerms || p.submitting}>
          {p.submitting ? 'Submitting…' : 'Submit for review →'}
        </button>
      </div>
    </div>
  );
}

function DoneStep({ onGo, location }: { onGo: () => void; location: Location }) {
  return (
    <div style={{ padding: 40, borderRadius: 18, background: 'var(--card)' }}>
      <div className="display" style={{ fontSize: 36, marginBottom: 14 }}>Thanks, {location.contact_first_name || 'there'}.</div>
      <p style={{ color: 'var(--ink-soft)', marginTop: 0, fontSize: 17, maxWidth: 560 }}>
        We'll review <strong>{location.bar_name}</strong> and get back to you in the next few hours. Your vendor account is already created — check <strong>{location.contact_email}</strong> for the welcome email and the magic link to sign in.
      </p>
      <div className="row" style={{ marginTop: 24, gap: 12 }}>
        <button onClick={onGo} className="btn btn-primary">Go to dashboard →</button>
        <Link to="/" className="btn btn-ghost">Back home</Link>
      </div>
    </div>
  );
}

// ---------- Shared widgets ----------
function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div className="display" style={{ fontSize: 22, letterSpacing: '-0.02em' }}>{title}</div>
      {hint && <div style={{ color: 'var(--ink-soft)', fontSize: 14, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label>
      <span className="field-label">{label}{required && <span style={{ color: 'var(--error)', marginLeft: 4 }}>*</span>}</span>
      {children}
      {hint && <div className="field-hint">{hint}</div>}
    </label>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ padding: 24, borderRadius: 18, background: 'var(--card)' }}>
      <div className="mono" style={{ fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="row-between" style={{ padding: '8px 0' }}>
      <span className="muted">{k}</span>
      <span style={{ textAlign: 'right', maxWidth: '60%' }}>{v}</span>
    </div>
  );
}

function TimeSelect({ value, onChange, startHour, endHour }: { value: string; onChange: (v: string) => void; startHour: number; endHour: number }) {
  const options: { value: string; label: string }[] = [];
  for (let h = startHour; h <= endHour; h++) {
    for (const m of [0, 30]) {
      const v = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      options.push({ value: v, label: formatHour(v) });
    }
  }
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

function formatHour(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number);
  const period = h >= 12 ? 'pm' : 'am';
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${h12}${m > 0 ? ':' + String(m).padStart(2, '0') : ''}${period}`;
}

function RangeSlider({ min, max, valueMin, valueMax, onChange }: { min: number; max: number; valueMin: number; valueMax: number; onChange: (mn: number, mx: number) => void }) {
  const pctMin = ((valueMin - min) / (max - min)) * 100;
  const pctMax = ((valueMax - min) / (max - min)) * 100;
  return (
    <div>
      <div className="row-between" style={{ marginBottom: 12 }}>
        <span style={{ fontSize: 14, color: 'var(--ink-soft)' }}>Min</span>
        <div style={{ fontSize: 16 }}>
          <strong>{valueMin}</strong> – <strong>{valueMax}</strong> guests
        </div>
        <span style={{ fontSize: 14, color: 'var(--ink-soft)' }}>Max</span>
      </div>
      <div style={{ position: 'relative', height: 32, padding: '14px 0' }}>
        <div style={{ position: 'absolute', left: 0, right: 0, top: 14, height: 4, background: 'var(--line)', borderRadius: 2 }} />
        <div style={{ position: 'absolute', left: `${pctMin}%`, right: `${100 - pctMax}%`, top: 14, height: 4, background: 'var(--ink)', borderRadius: 2 }} />
        <input
          type="range" min={min} max={max} value={valueMin}
          onChange={(e) => onChange(Math.min(Number(e.target.value), valueMax - 1), valueMax)}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', appearance: 'none', background: 'transparent', height: 32, padding: 0, border: 'none', pointerEvents: 'all' }}
          className="range-thumb"
        />
        <input
          type="range" min={min} max={max} value={valueMax}
          onChange={(e) => onChange(valueMin, Math.max(Number(e.target.value), valueMin + 1))}
          style={{ position: 'absolute', top: 0, left: 0, width: '100%', appearance: 'none', background: 'transparent', height: 32, padding: 0, border: 'none', pointerEvents: 'all' }}
          className="range-thumb"
        />
      </div>
    </div>
  );
}
